import React, { useEffect, useRef, useState } from 'react';
import { A11y, Autoplay, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { FormattedMessage } from 'react-intl';

import Ok from '../../../assets/icons/okInCircleWhite.svg';
import Right from '../../../assets/icons/carouselArrowRight.svg';
import Left from '../../../assets/icons/carouselArrowLeft.svg';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';

import { Image } from '../../Image/Image';
import { RichText } from '../../RichText/RichText';
import { Text } from '../../Text/Text';

import { PageComponent } from '../../PageComponent/PageComponent';

import { Column } from '../../Grid';

import { A11yText } from '../../A11yText/A11yText';

import * as classes from './CarouselWithImages.module.scss';
import { CarouselWithImagesProps } from './CarouselWithImages.types';

const useSwiperRef = <T extends HTMLElement>(): [T | null, React.Ref<T>] => {
    const [wrapper, setWrapper] = useState<T | null>(null);
    const ref = useRef<T>(null);

    useEffect(() => {
        if (ref.current) {
            setWrapper(ref.current);
        }
    }, []);

    return [wrapper, ref];
};

const CarouselWithImages: React.FC<CarouselWithImagesProps> = ({ elements, title, subTitle }) => {
    const [nextEl, nextElRef] = useSwiperRef<HTMLButtonElement>();
    const [prevEl, prevElRef] = useSwiperRef<HTMLButtonElement>();

    return (
        <>
            <PageComponent noSpacing>
                <div className={classes.titleContainer}>
                    <Column sizes={[12, 7, 7]}>
                        <Text weight="regular" variant="heading3" tag="h2">
                            <RichText textSize="heading3" content={title} />
                        </Text>
                    </Column>
                </div>
                {subTitle && (
                    <div className={classes.subTitleContainer}>
                        <Column sizes={[12, 7, 7]}>
                            <Text variant="sectionTitle">
                                <RichText isTitle={true} titleWeight="regular" textSize="heading6" content={subTitle} />
                            </Text>
                        </Column>
                    </div>
                )}
            </PageComponent>
            <Swiper
                modules={[Navigation, A11y, Autoplay]}
                slidesPerView={1.5}
                breakpoints={{
                    550: {
                        slidesPerView: 2.5,
                    },
                    1280: {
                        slidesPerView: 3.5,
                    },
                }}
                spaceBetween={15}
                freeMode={true}
                speed={2000}
                autoplay={{ delay: 3500, pauseOnMouseEnter: true, disableOnInteraction: false }}
                navigation={{
                    prevEl,
                    nextEl,
                }}
                effect="slide"
            >
                {elements.map((el, i) => {
                    if (el.content?.__typename === 'ContentfulGenericTextWithImage') {
                        return (
                            <SwiperSlide key={i} className={classes.customSwiperSlide}>
                                <div className={classes.imageTitle}>
                                    <div className={classes.iconTextContainer}>
                                        {el.content.title2 && (
                                            <>
                                                <Ok />
                                                <Text noMargin variant="heading4">
                                                    {el.content.title2}
                                                </Text>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {el.image && (
                                    <Image
                                        key={i}
                                        image={el.image}
                                        wrapperTag={'div'}
                                        classWrapper={classes.imageCointainer}
                                    />
                                )}
                            </SwiperSlide>
                        );
                    }
                })}
                <div className={classes.arrowsContainer}>
                    <button className={classes.arrow} ref={prevElRef}>
                        <Left />
                        <A11yText>
                            <FormattedMessage id="carousel.goleft" />
                        </A11yText>
                    </button>
                    <button className={classes.arrow} ref={nextElRef}>
                        <Right />
                        <A11yText>
                            <FormattedMessage id="carousel.goright" />
                        </A11yText>
                    </button>
                </div>
            </Swiper>
        </>
    );
};

export default CarouselWithImages;
